import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Locations from './components/Locations';
import Excellence from './components/Excellence';
import SafetyStandards from './components/SafetyStandards';
import MedicalRequirements from './components/MedicalRequirements';
import JumpDiaries from './components/JumpDiaries';
import Franchise from './components/Franchise';
import Gallery from './components/Gallery';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import MediaShowcase from './components/MediaShowcase';
import StatsStrip from './components/StatsStrip';
import PromoBanner from './components/PromoBanner';
import InclinedBungyPromo from './components/InclinedBungyPromo';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import FloatingButtons from './components/FloatingButtons';
import LocationsPage from './pages/LocationsPage';
import LocationDetailPage from './pages/LocationDetailPage';
import AboutPage from './pages/AboutPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import BookingSuccessPage from './pages/BookingSuccessPage';
import BookingFailurePage from './pages/BookingFailurePage';
import RefundPage from './pages/RefundPage';
import CancellationPage from './pages/CancellationPage';
import SafetyPage from './pages/SafetyPage';
import BlogsPage from './pages/BlogsPage';
import BlogDetailPage from './pages/BlogDetailPage';
import FranchisePage from './pages/FranchisePage';
import MOTPage from './pages/MOTPage';
import SANZPage from './pages/SANZPage';
import ATOAIPage from './pages/ATOAIPage';
import GoaTourismPage from './pages/GoaTourismPage';
import SafetyStandardsPage from './pages/SafetyStandardsPage';
import EquipmentsPage from './pages/EquipmentsPage';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { AgentAuthProvider } from './contexts/AgentAuthContext';
import PrivateRoute from './components/PrivateRoute';
import AgentProtectedRoute from './components/AgentProtectedRoute';
import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import BlogsManager from './admin/BlogsManager';
import CouponsManager from './admin/CouponsManager';
import MediaManager from './admin/MediaManager';
import LocationsManager from './admin/LocationsManager';
import ExcellenceManager from './admin/ExcellenceManager';
import TestimonialsManager from './admin/TestimonialsManager';
import BookingsManager from './admin/BookingsManager';
import AgenciesWrapper from './admin/AgenciesWrapper';
import ReceptionManager from './admin/ReceptionManager';
import BungyManManager from './admin/BungyManManager';
import SiteManagement from './admin/SiteManagement';
import WaiverFormPage from './pages/WaiverFormPage';
import AgentLogin from './agent/AgentLogin';
import AgentRegister from './agent/AgentRegister';
import AgentDashboard from './agent/AgentDashboard';
import AgentBookingForm from './agent/AgentBookingForm';
import AgentSlotsViewer from './agent/AgentSlotsViewer';
import AgentMessageSender from './agent/AgentMessageSender';

const HomePage = () => {
  React.useEffect(() => {
    // Force scroll to top immediately
    window.scrollTo(0, 0);
    
    // Remove any hash from URL (for search engine results)
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    
    // Additional safety check after a short delay
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <Hero />
      <Locations />
      <StatsStrip />
      <SafetyStandards />
      <MediaShowcase />
      <MedicalRequirements />
      <InclinedBungyPromo />
      <Excellence />
      <Franchise />
      <Testimonials />
      <Contact />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AdminAuthProvider>
          <AgentAuthProvider>
            <Routes>
              {/* Admin Routes - No Navbar/Footer */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/*" element={
                <PrivateRoute>
                  <AdminLayout />
                </PrivateRoute>
              }>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="media" element={<MediaManager />} />
                <Route path="locations" element={<LocationsManager />} />
                <Route path="bookings" element={<BookingsManager />} />
                <Route path="site-management" element={<SiteManagement />} />
                <Route path="agencies" element={<AgenciesWrapper />} />
                <Route path="testimonials" element={<TestimonialsManager />} />
                <Route path="excellence" element={<ExcellenceManager />} />
                <Route path="coupons" element={<CouponsManager />} />
                <Route path="blogs" element={<BlogsManager />} />
                <Route path="reception" element={<ReceptionManager />} />
                <Route path="bungyman" element={<BungyManManager />} />
                <Route path="contacts" element={<div className="text-white">Contacts Manager Coming Soon</div>} />
              </Route>

              {/* Agent Routes - No Navbar/Footer */}
              <Route path="/agent/login" element={<AgentLogin />} />
              <Route path="/agent/register" element={<AgentRegister />} />
              <Route path="/agent/dashboard" element={
                <AgentProtectedRoute>
                  <AgentDashboard />
                </AgentProtectedRoute>
              } />
              <Route path="/agent/booking/create" element={
                <AgentProtectedRoute>
                  <AgentBookingForm />
                </AgentProtectedRoute>
              } />
              <Route path="/agent/slots" element={
                <AgentProtectedRoute>
                  <AgentSlotsViewer />
                </AgentProtectedRoute>
              } />
              <Route path="/agent/send-message" element={
                <AgentProtectedRoute>
                  <AgentMessageSender />
                </AgentProtectedRoute>
              } />

            {/* Public Routes - With Navbar/Footer */}
            <Route path="*" element={
              <>
                <Navbar />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/about" element={<div className="pt-20"><AboutPage /></div>} />
                  <Route path="/about-us" element={<div className="pt-20"><AboutPage /></div>} />
                  <Route path="/locations" element={<div className="pt-20"><LocationsPage /></div>} />
                  <Route path="/location/:locationName" element={<div className="pt-20"><LocationDetailPage /></div>} />
                  <Route path="/book" element={<div className="pt-20 min-h-screen bg-black flex items-center justify-center"><h1 className="text-white text-4xl font-bold">Booking Page Coming Soon</h1></div>} />
                  <Route path="/gallery" element={<div className="pt-20 bg-black"><Gallery /></div>} />
                  <Route path="/franchise" element={<div className="pt-20"><FranchisePage /></div>} />
                  <Route path="/contact" element={<div className="pt-20 bg-black"><Contact /></div>} />
                  <Route path="/terms" element={<div className="pt-20"><TermsPage /></div>} />
                  <Route path="/privacy" element={<div className="pt-20"><PrivacyPage /></div>} />
                  <Route path="/booking-success" element={<div className="pt-20"><BookingSuccessPage /></div>} />
                  <Route path="/booking-failure" element={<div className="pt-20"><BookingFailurePage /></div>} />
                  <Route path="/refund" element={<div className="pt-20"><RefundPage /></div>} />
                  <Route path="/cancellation" element={<div className="pt-20"><CancellationPage /></div>} />
                  <Route path="/safety" element={<div className="pt-20"><SafetyPage /></div>} />
                  <Route path="/blogs" element={<div className="pt-20"><BlogsPage /></div>} />
                  <Route path="/blog/:slug" element={<div className="pt-20"><BlogDetailPage /></div>} />
                  <Route path="/mot" element={<div className="pt-20"><MOTPage /></div>} />
                  <Route path="/sanz" element={<div className="pt-20"><SANZPage /></div>} />
                  <Route path="/atoai" element={<div className="pt-20"><ATOAIPage /></div>} />
                  <Route path="/goa-tourism" element={<div className="pt-20"><GoaTourismPage /></div>} />
                  <Route path="/safety-standards" element={<div className="pt-20"><SafetyStandardsPage /></div>} />
                  <Route path="/equipments" element={<div className="pt-20"><EquipmentsPage /></div>} />
                  <Route path="/web-checkin" element={<WaiverFormPage />} />
                  <Route path="/web-checkin/:bookingId" element={<WaiverFormPage />} />
                  <Route path="/coming-soon" element={<div className="pt-20 min-h-screen bg-black flex items-center justify-center"><h1 className="text-white text-4xl font-bold">Coming Soon</h1></div>} />
                </Routes>
                <Footer />
                <FloatingButtons />
              </>
            } />
            </Routes>
          </AgentAuthProvider>
        </AdminAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;